import { useEffect, useMemo, useRef, useState } from 'react'
import { parseXlsx } from '@/lib/import/parseXlsx'
import type { ImportRow, ParseResult } from '@/lib/import/types'
import { diffAgainstDb } from '@/lib/import/diffAgainstDb'
import type { AsociadoDiff, CuentaDiff, DiffResult, DiffStatus } from '@/lib/import/diffAgainstDb'
import { applyImport, fetchHistory } from '@/lib/import/applyPlan'
import type { ApplyProgress, ApplyResult, ImportRunLog } from '@/lib/import/applyPlan'
import { formatDateTime, formatQ, formatRelative } from '@/lib/format'
import { PaginationBar, usePagination } from '@/components/Pagination'

type Tab = 'asociados' | 'cuentas' | 'warnings'
type DiffTab = 'asoc_nuevos' | 'asoc_mod' | 'cta_nuevas' | 'cta_mod' | 'warnings'

export function ImportPage() {
  const fileRef = useRef<HTMLInputElement | null>(null)
  const [parsing, setParsing] = useState(false)
  const [result, setResult] = useState<ParseResult | null>(null)
  const [error, setError] = useState<string | null>(null)
  const [tab, setTab] = useState<Tab>('asociados')

  // Diff state
  const [diffing, setDiffing] = useState(false)
  const [diff, setDiff] = useState<DiffResult | null>(null)
  const [diffError, setDiffError] = useState<string | null>(null)
  const [diffTab, setDiffTab] = useState<DiffTab>('asoc_nuevos')

  // Apply state
  const [applying, setApplying] = useState(false)
  const [applyProgress, setApplyProgress] = useState<ApplyProgress | null>(null)
  const [applyResult, setApplyResult] = useState<ApplyResult | null>(null)
  const [applyError, setApplyError] = useState<string | null>(null)

  // Historial
  const [history, setHistory] = useState<ImportRunLog[]>([])
  const [historyLoading, setHistoryLoading] = useState(false)

  const loadHistory = async () => {
    setHistoryLoading(true)
    try {
      const h = await fetchHistory(10)
      setHistory(h)
    } catch {
      // silencioso: el historial es auxiliar
    } finally {
      setHistoryLoading(false)
    }
  }

  useEffect(() => {
    loadHistory()
  }, [])

  const onPick = async (file: File | null) => {
    if (!file) return
    setParsing(true)
    setError(null)
    setResult(null)
    setDiff(null)
    setDiffError(null)
    setApplyResult(null)
    setApplyError(null)
    setApplyProgress(null)
    try {
      const r = await parseXlsx(file)
      setResult(r)
    } catch (e: unknown) {
      setError(e instanceof Error ? e.message : 'Error desconocido parseando el archivo.')
    } finally {
      setParsing(false)
    }
  }

  const clear = () => {
    setResult(null)
    setError(null)
    setDiff(null)
    setDiffError(null)
    setApplyResult(null)
    setApplyError(null)
    setApplyProgress(null)
    if (fileRef.current) fileRef.current.value = ''
  }

  const runApply = async () => {
    if (!result || !diff) return
    setApplying(true)
    setApplyError(null)
    setApplyResult(null)
    try {
      const r = await applyImport(result, diff, {
        onProgress: (p) => setApplyProgress(p),
      })
      setApplyResult(r)
      await loadHistory()
      // Rehacemos el diff para que quede "0 modificados" luego del apply.
      const newDiff = await diffAgainstDb(result)
      setDiff(newDiff)
    } catch (e: unknown) {
      setApplyError(e instanceof Error ? e.message : 'Error desconocido aplicando el import.')
    } finally {
      setApplying(false)
    }
  }

  const runDiff = async () => {
    if (!result) return
    setDiffing(true)
    setDiffError(null)
    try {
      const d = await diffAgainstDb(result)
      setDiff(d)
      // Saltar al primer tab que tenga algo
      if (d.counts.asociadosNuevos > 0) setDiffTab('asoc_nuevos')
      else if (d.counts.asociadosModificados > 0) setDiffTab('asoc_mod')
      else if (d.counts.cuentasNuevas > 0) setDiffTab('cta_nuevas')
      else if (d.counts.cuentasModificadas > 0) setDiffTab('cta_mod')
      else setDiffTab('warnings')
    } catch (e: unknown) {
      setDiffError(e instanceof Error ? e.message : 'Error desconocido comparando con DB.')
    } finally {
      setDiffing(false)
    }
  }

  // Proyecciones para tabs del parse
  const asociadosUnicos = useMemo(() => {
    if (!result) return []
    const seen = new Set<string>()
    const out: ImportRow[] = []
    for (const r of result.rows) {
      if (seen.has(r.no_asociado)) continue
      seen.add(r.no_asociado)
      out.push(r)
    }
    return out
  }, [result])

  return (
    <div style={{ display: 'grid', gap: 16 }}>
      {/* Drop zone / file picker */}
      <div className="card">
        <h2 style={{ margin: 0, fontSize: 16 }}>Subir export del core</h2>
        <p style={{ margin: '6px 0 14px', color: 'var(--n-500)', fontSize: 13, lineHeight: 1.55 }}>
          Tomá el xlsx que descargás del core actual y subilo acá. El archivo se
          procesa localmente en tu navegador; no se sube a ningún servidor hasta
          que confirmés "Aplicar".
        </p>

        <div
          onClick={() => fileRef.current?.click()}
          onDragOver={(e) => e.preventDefault()}
          onDrop={(e) => {
            e.preventDefault()
            onPick(e.dataTransfer.files[0] ?? null)
          }}
          style={{
            border: '1.5px dashed var(--n-200)',
            borderRadius: 10,
            padding: '28px 20px',
            textAlign: 'center',
            background: 'var(--bg)',
            cursor: parsing ? 'wait' : 'pointer',
            opacity: parsing ? 0.6 : 1,
            transition: 'background .15s',
          }}
        >
          <div style={{ fontWeight: 500, fontSize: 14, marginBottom: 4 }}>
            {parsing ? 'Parseando…' : 'Arrastrá el xlsx o hacé clic para elegir'}
          </div>
          <div style={{ color: 'var(--n-500)', fontSize: 12 }}>
            .xlsx del export del core · procesado localmente en tu navegador
          </div>
          <input
            ref={fileRef}
            type="file"
            accept=".xlsx,.xlsm,application/vnd.openxmlformats-officedocument.spreadsheetml.sheet"
            hidden
            onChange={(e) => onPick(e.target.files?.[0] ?? null)}
          />
        </div>

        {error ? (
          <div className="error-banner" style={{ marginTop: 14 }}>
            {error}
          </div>
        ) : null}
      </div>

      {/* Resumen del parse */}
      {result ? (
        <>
          <div className="card">
            <div
              style={{
                display: 'flex',
                justifyContent: 'space-between',
                alignItems: 'center',
                gap: 16,
                flexWrap: 'wrap',
              }}
            >
              <div>
                <div style={{ fontWeight: 600, fontSize: 14 }}>{result.fileName}</div>
                <div
                  style={{
                    color: 'var(--n-500)',
                    fontSize: 12,
                    fontFamily: 'ui-monospace,monospace',
                  }}
                >
                  {(result.fileSize / 1024).toFixed(1)} KB · sha256{' '}
                  {result.fileHash.slice(0, 12)}…
                </div>
              </div>
              <button className="btn btn-ghost" onClick={clear}>
                Descartar
              </button>
            </div>

            <div
              style={{
                display: 'grid',
                gridTemplateColumns: 'repeat(auto-fit, minmax(140px, 1fr))',
                gap: 10,
                marginTop: 14,
              }}
            >
              <Stat label="Filas leídas" value={result.rows.length} />
              <Stat label="Asociados únicos" value={result.asociadosUnique} />
              <Stat label="Cuentas únicas" value={result.cuentasUnique} />
              <Stat
                label="Warnings del parse"
                value={result.warnings.length}
                warn={result.warnings.length > 0}
              />
            </div>
          </div>

          {/* Tabs del parse (solo si no hicimos diff todavia) */}
          {!diff ? (
            <div className="card" style={{ padding: 0, overflow: 'hidden' }}>
              <div
                className="filters"
                style={{ padding: '12px 16px 0', borderBottom: '1px solid var(--n-100)' }}
              >
                <TabBtn current={tab} target="asociados" onClick={setTab}>
                  Asociados · {result.asociadosUnique}
                </TabBtn>
                <TabBtn current={tab} target="cuentas" onClick={setTab}>
                  Cuentas · {result.rows.length}
                </TabBtn>
                <TabBtn current={tab} target="warnings" onClick={setTab}>
                  Warnings · {result.warnings.length}
                </TabBtn>
              </div>

              {tab === 'asociados' ? (
                <AsociadosTable rows={asociadosUnicos} />
              ) : tab === 'cuentas' ? (
                <CuentasTable rows={result.rows} />
              ) : (
                <WarningsList warnings={result.warnings} />
              )}
            </div>
          ) : null}

          {/* CTA al Paso 2 */}
          {!diff ? (
            <div className="card" style={{ background: 'var(--bg)' }}>
              <div style={{ fontWeight: 500, fontSize: 14, marginBottom: 4 }}>
                Siguiente paso: comparar con la DB
              </div>
              <div style={{ color: 'var(--n-500)', fontSize: 13, lineHeight: 1.55 }}>
                Al tocar "Comparar contra DB" voy a consultar los asociados y
                cuentas actuales y clasificar cada fila del xlsx como{' '}
                <strong>nueva</strong>, <strong>modificada</strong> o{' '}
                <strong>sin cambios</strong>. Nada se escribe todavía.
              </div>
              {diffError ? (
                <div className="error-banner" style={{ marginTop: 12 }}>
                  {diffError}
                </div>
              ) : null}
              <button
                className="btn btn-primary"
                style={{ marginTop: 12 }}
                onClick={runDiff}
                disabled={diffing}
              >
                {diffing ? 'Comparando…' : 'Comparar contra DB'}
              </button>
            </div>
          ) : null}

          {/* Resultado del diff */}
          {diff ? (
            <>
              <DiffPanel
                diff={diff}
                diffTab={diffTab}
                setDiffTab={setDiffTab}
                onReset={() => setDiff(null)}
              />
              <ApplyPanel
                diff={diff}
                applying={applying}
                applyProgress={applyProgress}
                applyResult={applyResult}
                applyError={applyError}
                onRun={runApply}
              />
            </>
          ) : null}
        </>
      ) : null}

      {/* Historial de imports */}
      <HistoryPanel rows={history} loading={historyLoading} />
    </div>
  )
}

function HistoryPanel({ rows, loading }: { rows: ImportRunLog[]; loading: boolean }) {
  return (
    <div className="card" style={{ padding: 0, overflow: 'hidden' }}>
      <div style={{ padding: '14px 16px', borderBottom: '1px solid var(--n-100)' }}>
        <div style={{ fontWeight: 600, fontSize: 14 }}>Historial de imports</div>
        <div style={{ color: 'var(--n-500)', fontSize: 12, marginTop: 2 }}>
          Últimos 10 registros de <code>core_import_run</code>.
        </div>
      </div>
      {loading && rows.length === 0 ? (
        <div className="empty" style={{ padding: 24 }}>
          Cargando historial…
        </div>
      ) : rows.length === 0 ? (
        <div className="empty" style={{ padding: 24 }}>
          <div className="empty-title">Sin imports aún</div>
          <div className="empty-sub">
            El primer import que apliques acá va a aparecer listado.
          </div>
        </div>
      ) : (
        <div className="table-wrap" style={{ border: 0, borderRadius: 0 }}>
          <table className="table">
            <thead>
              <tr>
                <th>Archivo</th>
                <th>Estado</th>
                <th>Filas</th>
                <th>Iniciado</th>
                <th>Finalizado</th>
                <th>Hash</th>
              </tr>
            </thead>
            <tbody>
              {rows.map((r) => (
                <tr key={r.id}>
                  <td style={{ fontSize: 13 }}>{r.archivo_nombre}</td>
                  <td>
                    <RunStatusBadge status={r.status} />
                  </td>
                  <td className="mono" style={{ fontSize: 12 }}>
                    {r.rows_ok ?? 0} / {r.rows_total ?? 0}
                  </td>
                  <td>
                    <div style={{ fontSize: 12 }}>{formatRelative(r.started_at)}</div>
                    <div className="muted" style={{ fontSize: 11 }}>
                      {formatDateTime(r.started_at)}
                    </div>
                  </td>
                  <td className="muted" style={{ fontSize: 11 }}>
                    {r.finished_at ? formatDateTime(r.finished_at) : '—'}
                  </td>
                  <td className="mono" style={{ fontSize: 11 }}>
                    {r.archivo_hash.slice(0, 10)}…
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  )
}

function RunStatusBadge({ status }: { status: ImportRunLog['status'] }) {
  const map: Record<
    ImportRunLog['status'],
    { label: string; color: string; bg: string }
  > = {
    pending: { label: 'Pendiente', color: 'var(--n-700)', bg: 'var(--n-100)' },
    running: { label: 'Corriendo', color: 'var(--brand-700)', bg: 'var(--brand-50)' },
    ok: { label: 'OK', color: 'var(--success)', bg: '#dcfce7' },
    partial: { label: 'Parcial', color: 'var(--warning)', bg: '#fef3c7' },
    failed: { label: 'Falló', color: 'var(--error)', bg: '#fee2e2' },
  }
  const s = map[status]
  return (
    <span
      style={{
        display: 'inline-block',
        padding: '2px 8px',
        borderRadius: 999,
        fontSize: 11,
        fontWeight: 500,
        color: s.color,
        background: s.bg,
      }}
    >
      {s.label}
    </span>
  )
}

// -----------------------------------------------------------------------------
// Panel del diff (Paso 2)
// -----------------------------------------------------------------------------

function DiffPanel({
  diff,
  diffTab,
  setDiffTab,
  onReset,
}: {
  diff: DiffResult
  diffTab: DiffTab
  setDiffTab: (t: DiffTab) => void
  onReset: () => void
}) {
  const { counts } = diff
  const hasErrors = diff.warnings.some((w) => w.severity === 'error')

  return (
    <>
      {/* Resumen del diff */}
      <div className="card">
        <div
          style={{
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
            gap: 16,
            marginBottom: 14,
          }}
        >
          <h2 style={{ margin: 0, fontSize: 16 }}>Comparación contra DB</h2>
          <button className="btn btn-ghost" onClick={onReset}>
            Rehacer
          </button>
        </div>

        <div
          style={{
            display: 'grid',
            gridTemplateColumns: 'repeat(auto-fit, minmax(160px, 1fr))',
            gap: 10,
          }}
        >
          <DiffStat label="Asociados nuevos" value={counts.asociadosNuevos} tone="new" />
          <DiffStat label="Asociados modificados" value={counts.asociadosModificados} tone="mod" />
          <DiffStat
            label="Asociados sin cambios"
            value={counts.asociadosSinCambios}
            tone="none"
          />
          <DiffStat label="Cuentas nuevas" value={counts.cuentasNuevas} tone="new" />
          <DiffStat label="Cuentas modificadas" value={counts.cuentasModificadas} tone="mod" />
          <DiffStat label="Cuentas sin cambios" value={counts.cuentasSinCambios} tone="none" />
        </div>
      </div>

      {/* Warnings del diff */}
      {diff.warnings.length > 0 ? (
        <div className="card" style={{ borderColor: hasErrors ? 'var(--error)' : undefined }}>
          <div style={{ fontWeight: 600, fontSize: 14, marginBottom: 8 }}>
            Observaciones del diff
          </div>
          <ul style={{ margin: 0, paddingLeft: 18, display: 'grid', gap: 6 }}>
            {diff.warnings.map((w, i) => (
              <li
                key={i}
                style={{
                  fontSize: 13,
                  color: w.severity === 'error' ? 'var(--error)' : 'var(--n-700)',
                }}
              >
                <strong>{w.severity === 'error' ? 'Error' : 'Warning'}:</strong> {w.message}
              </li>
            ))}
          </ul>
        </div>
      ) : null}

      {/* Tabs del diff */}
      <div className="card" style={{ padding: 0, overflow: 'hidden' }}>
        <div
          className="filters"
          style={{ padding: '12px 16px 0', borderBottom: '1px solid var(--n-100)' }}
        >
          <TabBtn current={diffTab} target="asoc_nuevos" onClick={setDiffTab}>
            Asoc. nuevos · {counts.asociadosNuevos}
          </TabBtn>
          <TabBtn current={diffTab} target="asoc_mod" onClick={setDiffTab}>
            Asoc. modificados · {counts.asociadosModificados}
          </TabBtn>
          <TabBtn current={diffTab} target="cta_nuevas" onClick={setDiffTab}>
            Cuentas nuevas · {counts.cuentasNuevas}
          </TabBtn>
          <TabBtn current={diffTab} target="cta_mod" onClick={setDiffTab}>
            Cuentas modificadas · {counts.cuentasModificadas}
          </TabBtn>
        </div>

        {diffTab === 'asoc_nuevos' ? (
          <AsociadosDiffTable rows={diff.asociados.filter((a) => a.status === 'nuevo')} showChanges={false} />
        ) : diffTab === 'asoc_mod' ? (
          <AsociadosDiffTable rows={diff.asociados.filter((a) => a.status === 'modificado')} showChanges />
        ) : diffTab === 'cta_nuevas' ? (
          <CuentasDiffTable rows={diff.cuentas.filter((c) => c.status === 'nuevo')} showChanges={false} />
        ) : (
          <CuentasDiffTable rows={diff.cuentas.filter((c) => c.status === 'modificado')} showChanges />
        )}
      </div>
    </>
  )
}

function ApplyPanel({
  diff,
  applying,
  applyProgress,
  applyResult,
  applyError,
  onRun,
}: {
  diff: DiffResult
  applying: boolean
  applyProgress: ApplyProgress | null
  applyResult: ApplyResult | null
  applyError: string | null
  onRun: () => void
}) {
  const hasErrors = diff.warnings.some((w) => w.severity === 'error')
  const totalChanges =
    diff.counts.asociadosNuevos +
    diff.counts.asociadosModificados +
    diff.counts.cuentasNuevas +
    diff.counts.cuentasModificadas

  // Post-apply summary
  if (applyResult) {
    const ok = applyResult.status === 'ok'
    return (
      <div className="card" style={{ background: ok ? '#dcfce7' : '#fef3c7', borderColor: ok ? 'var(--success)' : 'var(--warning)' }}>
        <div style={{ fontWeight: 600, fontSize: 15, marginBottom: 6 }}>
          {ok ? 'Import aplicado' : `Import aplicado con estado "${applyResult.status}"`}
        </div>
        <div style={{ color: 'var(--n-700)', fontSize: 13, lineHeight: 1.55 }}>
          <div className="mono" style={{ fontSize: 12, marginBottom: 4 }}>
            run: {applyResult.runId}
          </div>
          Filas procesadas: <strong>{applyResult.rowsOk}</strong> · Errores:{' '}
          <strong>{applyResult.rowsFail}</strong>
        </div>
        {applyResult.errorSummary.length > 0 ? (
          <ul style={{ margin: '8px 0 0', paddingLeft: 18, fontSize: 12 }}>
            {applyResult.errorSummary.slice(0, 5).map((e, i) => (
              <li key={i}>{e}</li>
            ))}
            {applyResult.errorSummary.length > 5 ? (
              <li>… +{applyResult.errorSummary.length - 5} más</li>
            ) : null}
          </ul>
        ) : null}
      </div>
    )
  }

  return (
    <div className="card" style={{ background: 'var(--bg)' }}>
      <div style={{ fontWeight: 500, fontSize: 14, marginBottom: 4 }}>Aplicar el import</div>
      <div style={{ color: 'var(--n-500)', fontSize: 13, lineHeight: 1.55 }}>
        Upsert en lotes a <code>asociado</code> y <code>cuenta</code> con
        whitelist de columnas — campos NeoCore-only (auth_user_id, notas,
        flags) no se pisan. El run queda registrado en{' '}
        <code>core_import_run</code>.
      </div>

      {applyError ? (
        <div className="error-banner" style={{ marginTop: 12 }}>
          {applyError}
        </div>
      ) : null}

      {applying && applyProgress ? (
        <div style={{ marginTop: 12, fontSize: 13, color: 'var(--n-700)' }}>
          <div style={{ marginBottom: 4 }}>
            Fase: <strong>{applyProgress.phase}</strong> — {applyProgress.done} /{' '}
            {applyProgress.total}
          </div>
          <div
            style={{
              height: 6,
              background: 'var(--n-100)',
              borderRadius: 3,
              overflow: 'hidden',
            }}
          >
            <div
              style={{
                height: '100%',
                width: `${applyProgress.total > 0 ? (applyProgress.done / applyProgress.total) * 100 : 0}%`,
                background: 'var(--brand-500)',
                transition: 'width .25s',
              }}
            />
          </div>
        </div>
      ) : null}

      <button
        className="btn btn-primary"
        style={{ marginTop: 12 }}
        onClick={onRun}
        disabled={applying || hasErrors || totalChanges === 0}
        title={
          hasErrors
            ? 'Hay errores en el diff. Resolvelos antes de aplicar.'
            : totalChanges === 0
            ? 'No hay cambios para aplicar. Todo el xlsx ya está sincronizado.'
            : undefined
        }
      >
        {applying
          ? 'Aplicando…'
          : totalChanges === 0
          ? 'Sin cambios para aplicar'
          : `Aplicar (${totalChanges} cambios)`}
      </button>
    </div>
  )
}

// -----------------------------------------------------------------------------
// Componentes pequeños
// -----------------------------------------------------------------------------

function Stat({ label, value, warn }: { label: string; value: number; warn?: boolean }) {
  return (
    <div
      style={{
        padding: 12,
        borderRadius: 8,
        background: 'var(--bg)',
        border: '1px solid var(--n-100)',
      }}
    >
      <div
        style={{
          fontSize: 11,
          color: 'var(--n-500)',
          textTransform: 'uppercase',
          letterSpacing: 0.3,
        }}
      >
        {label}
      </div>
      <div
        style={{
          fontSize: 22,
          fontWeight: 600,
          fontVariantNumeric: 'tabular-nums',
          marginTop: 4,
          color: warn ? 'var(--error)' : 'inherit',
        }}
      >
        {value}
      </div>
    </div>
  )
}

function DiffStat({
  label,
  value,
  tone,
}: {
  label: string
  value: number
  tone: 'new' | 'mod' | 'none'
}) {
  const accent =
    tone === 'new'
      ? 'var(--success)'
      : tone === 'mod'
      ? 'var(--warning)'
      : 'var(--n-500)'
  return (
    <div
      style={{
        padding: 12,
        borderRadius: 8,
        background: 'var(--surface)',
        border: `1px solid ${value > 0 ? accent : 'var(--n-100)'}`,
      }}
    >
      <div
        style={{
          fontSize: 11,
          color: 'var(--n-500)',
          textTransform: 'uppercase',
          letterSpacing: 0.3,
        }}
      >
        {label}
      </div>
      <div
        style={{
          fontSize: 22,
          fontWeight: 600,
          fontVariantNumeric: 'tabular-nums',
          marginTop: 4,
          color: value > 0 ? accent : 'inherit',
        }}
      >
        {value}
      </div>
    </div>
  )
}

function TabBtn<T extends string>({
  current,
  target,
  onClick,
  children,
}: {
  current: T
  target: T
  onClick: (t: T) => void
  children: React.ReactNode
}) {
  return (
    <button
      className={'chip' + (current === target ? ' active' : '')}
      onClick={() => onClick(target)}
      style={{ borderRadius: 8 }}
    >
      {children}
    </button>
  )
}

function maskDpi(dpi: string): string {
  if (dpi.length !== 13) return dpi
  return dpi.slice(0, 3) + '*******' + dpi.slice(-3)
}

function StatusBadge({ status }: { status: DiffStatus }) {
  const map: Record<DiffStatus, { label: string; color: string; bg: string }> = {
    nuevo: { label: 'Nuevo', color: 'var(--success)', bg: '#dcfce7' },
    modificado: { label: 'Modificado', color: 'var(--warning)', bg: '#fef3c7' },
    sin_cambios: { label: 'Sin cambios', color: 'var(--n-500)', bg: 'var(--n-100)' },
  }
  const s = map[status]
  return (
    <span
      style={{
        display: 'inline-block',
        padding: '2px 8px',
        borderRadius: 999,
        fontSize: 11,
        fontWeight: 500,
        color: s.color,
        background: s.bg,
      }}
    >
      {s.label}
    </span>
  )
}

function AsociadosTable({ rows }: { rows: ImportRow[] }) {
  const pg = usePagination(rows, 20)
  return (
    <>
      <div className="table-wrap" style={{ border: 0, borderRadius: 0 }}>
        <table className="table">
          <thead>
            <tr>
              <th>No. Asociado</th>
              <th>Nombre</th>
              <th>DPI</th>
              <th>Teléfono</th>
              <th>Municipio</th>
              <th>Usuario core</th>
            </tr>
          </thead>
          <tbody>
            {pg.pageItems.map((r) => (
              <tr key={r.no_asociado}>
                <td className="mono">{r.no_asociado}</td>
                <td>{r.nombre_completo}</td>
                <td className="mono" style={{ fontSize: 12 }}>
                  {maskDpi(r.dpi)}
                </td>
                <td className="mono" style={{ fontSize: 12 }}>
                  {r.telefono_e164 ?? '—'}
                </td>
                <td>{r.municipio ?? '—'}</td>
                <td className="mono" style={{ fontSize: 12 }}>
                  {r.usuario_core}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      <PaginationBar state={pg} />
    </>
  )
}

function CuentasTable({ rows }: { rows: ImportRow[] }) {
  const pg = usePagination(rows, 20)
  return (
    <>
      <div className="table-wrap" style={{ border: 0, borderRadius: 0 }}>
        <table className="table">
          <thead>
            <tr>
              <th>Cuenta</th>
              <th>No. Asociado</th>
              <th>Tipo</th>
              <th>Saldo disponible</th>
              <th>Saldo total</th>
              <th>Estado</th>
            </tr>
          </thead>
          <tbody>
            {pg.pageItems.map((r) => (
              <tr key={r.cuenta_id}>
                <td className="mono" style={{ fontSize: 12.5 }}>
                  {r.cuenta_id}
                </td>
                <td className="mono" style={{ fontSize: 12 }}>
                  {r.no_asociado}
                </td>
                <td style={{ fontSize: 12 }}>{r.tipo_servicio}</td>
                <td className="mono">{formatQ(r.saldo_disponible)}</td>
                <td className="mono">{formatQ(r.saldo_total)}</td>
                <td>
                  <span
                    className={`badge badge-${r.estado === 'activa' ? 'enviado' : 'cancelado'}`}
                  >
                    {r.estado}
                  </span>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      <PaginationBar state={pg} />
    </>
  )
}

function AsociadosDiffTable({
  rows,
  showChanges,
}: {
  rows: AsociadoDiff[]
  showChanges: boolean
}) {
  const pg = usePagination(rows, 20)
  if (rows.length === 0) {
    return (
      <div className="empty" style={{ padding: 32 }}>
        <div className="empty-title">Nada por aquí</div>
        <div className="empty-sub">No hay registros en esta categoría.</div>
      </div>
    )
  }
  return (
    <>
      <div className="table-wrap" style={{ border: 0, borderRadius: 0 }}>
        <table className="table">
          <thead>
            <tr>
              <th>Estado</th>
              <th>No. Asociado</th>
              <th>Nombre</th>
              <th>DPI</th>
              {showChanges ? <th>Campos modificados</th> : null}
            </tr>
          </thead>
          <tbody>
            {pg.pageItems.map((a) => (
              <tr key={a.no_asociado}>
                <td>
                  <StatusBadge status={a.status} />
                </td>
                <td className="mono">{a.no_asociado}</td>
                <td>{a.nombre_completo}</td>
                <td className="mono" style={{ fontSize: 12 }}>
                  {maskDpi(a.dpi)}
                </td>
                {showChanges ? (
                  <td style={{ fontSize: 12 }}>
                    <span className="mono">{a.changedFields.join(', ')}</span>
                  </td>
                ) : null}
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      <PaginationBar state={pg} />
    </>
  )
}

function CuentasDiffTable({
  rows,
  showChanges,
}: {
  rows: CuentaDiff[]
  showChanges: boolean
}) {
  const pg = usePagination(rows, 20)
  if (rows.length === 0) {
    return (
      <div className="empty" style={{ padding: 32 }}>
        <div className="empty-title">Nada por aquí</div>
        <div className="empty-sub">No hay registros en esta categoría.</div>
      </div>
    )
  }
  return (
    <>
      <div className="table-wrap" style={{ border: 0, borderRadius: 0 }}>
        <table className="table">
          <thead>
            <tr>
              <th>Estado</th>
              <th>Cuenta</th>
              <th>Asociado</th>
              <th>Tipo</th>
              <th>Saldo total</th>
              {showChanges ? <th>Campos modificados</th> : null}
            </tr>
          </thead>
          <tbody>
            {pg.pageItems.map((c) => (
              <tr key={c.cuenta_id}>
                <td>
                  <StatusBadge status={c.status} />
                </td>
                <td className="mono" style={{ fontSize: 12.5 }}>
                  {c.cuenta_id}
                </td>
                <td className="mono" style={{ fontSize: 12 }}>
                  {c.no_asociado}
                </td>
                <td style={{ fontSize: 12 }}>{c.tipo_servicio}</td>
                <td className="mono">{formatQ(c.row.saldo_total)}</td>
                {showChanges ? (
                  <td style={{ fontSize: 12 }}>
                    <span className="mono">{c.changedFields.join(', ')}</span>
                  </td>
                ) : null}
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      <PaginationBar state={pg} />
    </>
  )
}

function WarningsList({ warnings }: { warnings: ParseResult['warnings'] }) {
  if (warnings.length === 0) {
    return (
      <div className="empty" style={{ padding: 32 }}>
        <div className="empty-title">Sin warnings</div>
        <div className="empty-sub">El parser no encontró filas problemáticas.</div>
      </div>
    )
  }
  return (
    <div style={{ padding: 16 }}>
      <ul style={{ margin: 0, paddingLeft: 18, display: 'grid', gap: 6 }}>
        {warnings.slice(0, 100).map((w, i) => (
          <li key={i} style={{ fontSize: 12.5, color: 'var(--n-700)' }}>
            <span className="mono" style={{ fontSize: 11, color: 'var(--n-500)' }}>
              [fila {w.rowIndex + 2}] [{w.field}]{' '}
            </span>
            {w.message}
          </li>
        ))}
        {warnings.length > 100 ? (
          <li style={{ color: 'var(--n-500)', fontSize: 12 }}>… +{warnings.length - 100} más</li>
        ) : null}
      </ul>
    </div>
  )
}
